"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getThreadMessages } from "../threads/threads";
import { queryKeys } from "../query/keys";
import { GC_TIME, REALTIME_STALE_TIME } from "../query/config";
import type { Message } from "../types/augmentations";
import { compareMessagesForRender } from "../messages/messages";

/**
 * Fetch messages for a thread.
 *
 * staleTime: 0 — always refetch on mount to catch messages from other sessions.
 * gcTime: 10 min — keeps messages cached for instant back-navigation.
 *
 * @param threadId - Thread ID
 * @param options - Additional options
 * @returns React Query result with messages data
 */
export function useMessages(
  threadId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.messages.list(threadId ?? ""),
    queryFn: async (): Promise<Message[]> => {
      if (!threadId) return [];
      const messages = await getThreadMessages(threadId);
      return messages ?? [];
    },
    staleTime: REALTIME_STALE_TIME, // Always refetch on mount
    gcTime: GC_TIME, // Keep cached for instant navigation
    enabled: options?.enabled ?? !!threadId,
  });
}

/**
 * Cache manipulation helpers for messages.
 *
 * Used by the SSE streaming hooks to write completed messages
 * to the React Query cache. Streaming content goes through local
 * state, NOT through these functions — only call upsertMessage
 * when a message is finalized (onDone).
 *
 * @param threadId - Thread ID to manipulate cache for
 * @returns Cache manipulation functions
 */
export function useMessagesCache(threadId: string | null) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.messages.list(threadId ?? "");

  return {
    /**
     * Insert or update a message in the cache.
     * If message.id exists, update in-place. Otherwise append.
     *
     * Cache key resolution: explicit `targetThreadId` wins, then the
     * message's own `thread_id`, then the hook-bound thread. The
     * fallback to `message.thread_id` matters during the first send in
     * a fresh chat — `setActiveThreadId(newId)` doesn't commit a
     * re-render before pollMessageStatus / SSE onDone fire, so the
     * captured `queryKey` would otherwise point at the empty-string
     * key and the assistant message would never appear.
     */
    upsertMessage: (message: Message, targetThreadId?: string) => {
      const resolvedThreadId =
        targetThreadId ?? (message.thread_id ?? undefined);
      const key = resolvedThreadId
        ? queryKeys.messages.list(resolvedThreadId)
        : queryKey;
      queryClient.setQueryData<Message[]>(key, (old = []) => {
        const idx = old.findIndex((m) => m.id === message.id);
        let next: Message[];
        if (idx >= 0) {
          const updated = [...old];
          updated[idx] = { ...updated[idx], ...message };
          next = updated;
        } else {
          next = [...old, message];
        }
        return [...next].sort(compareMessagesForRender);
      });
    },

    /**
     * Remove a message from the cache.
     */
    removeMessage: (messageId: string) => {
      queryClient.setQueryData<Message[]>(queryKey, (old = []) =>
        old.filter((m) => m.id !== messageId)
      );
    },

    /**
     * Replace the entire messages array in cache.
     */
    setMessages: (messages: Message[]) => {
      queryClient.setQueryData<Message[]>(
        queryKey,
        [...messages].sort(compareMessagesForRender)
      );
    },

    /**
     * Invalidate the messages cache to trigger a refetch.
     */
    invalidate: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  };
}
